import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { DashboardProductResultDto, DashboardProcessMonitoringDto } from '../../Dto_Home';
import { getMixedChartData, getItemGroupChartData, getBatchDefectDoughnutData } from '../../Utils_Home';
import { ChartOptions } from 'chart.js';

const MONITORING_COLUMN_STATE_KEY = 'home-b-process-monitoring-column-state';
const ignoreDashboardGridState = () => undefined;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface Props {
  monthlyProductionData: DashboardProductResultDto[];
  itemGroupData: DashboardProductResultDto[];
  batchDefectData: DashboardProductResultDto[];
  processMonitoringData: DashboardProcessMonitoringDto[];
}


// 차트 옵션 설정
export const chartOptions = {
  // 혼합 차트 옵션
  mixedChart: {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  } as ChartOptions<'bar'>,

  // 바 차트 옵션
  barChart: {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: '수량 (개)'
        }
      },
    },
  } as ChartOptions<'bar'>,

  // 도넛 차트 옵션
  doughnutChart: {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  } as ChartOptions<'doughnut'>,
};
 
// 공정 실적 모니터링 테이블 컬럼 정의
const monitoringColumns: ColDef[] = [
  ...getDefaultRowIndexColumnConfig((params: any) => {
    if (params.node.rowPinned === 'bottom') return params.data?.rowIndex || '';
    return params.node.rowIndex + 1;
  }),
  { field: 'workplaceNm', headerName: '공정명', width: 120 ,
    cellStyle: (params) =>
      params.node.rowPinned === 'bottom'
        ? {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            fontWeight: 600,
          }
        : undefined,  
  },
  { field: 'inQty', headerName: '투입량', width: 100, cellStyle: { textAlign: 'right' } },
  { field: 'productionQty', headerName: '생산량', width: 100, cellStyle: { textAlign: 'right' } },
];

// 공정 실적 모니터링 테이블 기본 설정
const monitoringDefaultColDef: ColDef = {
  resizable: true,
  sortable: true,
};

const ChartsSection_Pic: React.FC<Props> = ({
  monthlyProductionData, itemGroupData,
  batchDefectData, processMonitoringData
}) => {
  const mixedChartData = getMixedChartData(monthlyProductionData);
  const itemGroupChartData = getItemGroupChartData(itemGroupData);
  const doughnutData = getBatchDefectDoughnutData(batchDefectData);
  const monitoringGridRef = useRef<AgGridReact<DashboardProcessMonitoringDto> | null>(null);
  const monitoringPinnedBottomRow = useMemo(() => [{ 
    rowIndex: '', 
    workplaceNm: `${processMonitoringData.length.toLocaleString()}건`,
    productionQty: processMonitoringData.reduce(
      (sum, item) => sum + (Number(item.productionQty) || 0),
      0
    )
  }],
    [processMonitoringData]
  );

  return (
    <div className="grid grid-cols-4 gap-4">
      <div className="bg-white rounded-lg p-3 shadow-sm">
        <h2 className="text-lg font-semibold mb-2">브랜드별 생산실적 현황</h2>
        <div className="flex-1 min-h-0">
          <Bar data={mixedChartData as any} options={chartOptions.mixedChart} />
        </div>
      </div>

      <div className="bg-white rounded-lg p-3 shadow-sm">
        <h2 className="text-lg font-semibold mb-2">월별 불량현황</h2>
        <div className="flex-1 min-h-0">
          <Bar data={itemGroupChartData} options={chartOptions.barChart} />
        </div>
      </div>

      <div className="bg-white rounded-lg p-3 shadow-sm">
        <h2 className="text-lg font-semibold mb-2">품목그룹별 생산실적 현황</h2>
        <div className="flex-1 min-h-0">
          <Doughnut data={doughnutData} options={chartOptions.doughnutChart} />
        </div>
      </div>

      <div className="bg-white rounded-lg p-3 shadow-sm">
        <h2 className="text-lg font-semibold mb-2">공정 실적 모니터링</h2>
        <div className="ag-theme-alpine flex-1 min-h-0" style={{ height: '200px' }}>
          <GridContextWrapper<DashboardProcessMonitoringDto>
            gridRef={monitoringGridRef}
            paddedGridData={processMonitoringData}
            baseColumnDefs={monitoringColumns}
            setColumnState={ignoreDashboardGridState}
            localStorageKey={MONITORING_COLUMN_STATE_KEY}
            enableCellCalculation
            enableDataHistory={false}
          >
            <Grid_Touch<DashboardProcessMonitoringDto>
              ref={monitoringGridRef}
              rowData={processMonitoringData}
              columnDefs={monitoringColumns}
              pinnedBottomRowData={monitoringPinnedBottomRow}
              defaultColDef={monitoringDefaultColDef}
              setHoveredRowIndex={ignoreDashboardGridState}
              columnStateKey={MONITORING_COLUMN_STATE_KEY}
              setColumnState={ignoreDashboardGridState}
            />
          </GridContextWrapper>
        </div>
      </div>
    </div>
  );
};

export default ChartsSection_Pic;
